import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Input, Button, Form, Modal, ModalBody, Tooltip } from 'reactstrap';
import { FaPlus, FaCheck, FaTrash, FaArrowLeft, FaEdit } from 'react-icons/fa';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import LoadingOverlay from "react-loading-overlay-ts";
import DNALoader from "../../../components/Loaders/DnaLoader";
import Alerts from "../../../components/Alerts/alerts";
import DashboardService from "../dashboard.service";
import AuthService from "../../Authentication/auth.service";
import { useTranslation } from 'react-i18next';
import Select from 'react-select';
import moment from 'moment';

const OrderConfirmationModal = ({ isOpen, toggle, orderId, message }) => {
  return (
    <Modal isOpen={isOpen} toggle={toggle} centered className="order-confirmation-modal">
      <ModalBody className="text-center p-4">
        <div className="confirmation-icon mb-3">
          <FaCheck size={24} color="#fff" />
        </div>
        <h4 className="mb-2">{message || "Order Submitted"}</h4>
        <h2 className="order-id mb-0">ID# {orderId}</h2>
      </ModalBody>
    </Modal>
  );
};

const RFQForm = () => {
  const [items, setItems] = useState([]);
  const [allItems, setAllItems] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [orderId, setOrderId] = useState('');
  const [loading, setLoading] = useState(false);
  const [selectedItems, setSelectedItems] = useState(new Set());
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { id } = useParams();
  const [userInfo] = useState(JSON.parse(localStorage.getItem('user')));
  const [Fetched_Businesses, setFetched_Businesses] = useState([]);
  const [reason_of_use, setReason_of_use] = useState('Treatment of Patient');
  const [hasDraft, setHasDraft] = useState(false);
  const [draftId, setDraftId] = useState(null);
  const [tooltipOpen, setTooltipOpen] = useState({});

  const orderDetails = location.state?.orderDetails;
  const isEditMode = !!id;

  useEffect(() => {
    fetchAllItems();
    if (!isEditMode) {
      Fetch_Businesses();
      checkForExistingDraft();
    }
  }, [isEditMode]);

  useEffect(() => {
    if (allItems.length > 0) {
      if (isEditMode) {
        fetchRfqDetails();
      } else if (orderDetails) {
        prefillOrderDetails();
      }
    }
  }, [allItems, isEditMode, orderDetails]);

  const fetchAllItems = async () => {
    setLoading(true);
    try {
      const getData = {
        functionName: "GetDropDownItems",
        postData: {
          sort: { _id: -1 },
          offset: 0,
          limit: 99999,
          portal: 'customers',
          filter: {},
          business: []
        },
      };
      const response = await DashboardService.getAllItems(getData);
      if (response.data.message === "Unauthorized") {
        AuthService.logout();
        navigate("/login");
      } else {
        //iph todo: 47
        //console.log("response.data.data", response.data.data)
        const formattedItems = response.data.data.map(catItem => ({
          value: catItem._id.toString(),
          label: `${catItem?.item_name} ${catItem?.BaseUOM} (${catItem?.ItemGroup || "NP"})`,
          category: catItem.category,
          ItemGroup: catItem.ItemGroup,
          status: catItem.status,
          price: catItem.price,
          item_code: catItem.item_code,
          BaseUOM: catItem.BaseUOM

        }));
        setAllItems(formattedItems);
      }
    } catch (error) {
      Alerts.swalErrorAlert(t("Error fetching items"));
    }
    setLoading(false);
  };

  const Fetch_Businesses = async () => {
    try {
      const response = await DashboardService.GetBusinesses();
      if (response.data.message === "Unauthorized") {
        AuthService.logout();
        navigate("/login");
      } else {
        const business = response.data.data[0];
        setFetched_Businesses([business]);
      }
    } catch (error) {
      Alerts.swalErrorAlert(t("Error fetching businesses"));
    }
  };

  const checkForExistingDraft = async () => {
    try {
      const response = await DashboardService.GetRFQDraft(userInfo._id);
      if (response.data.status && response.data.data) {
        setHasDraft(true);
        setDraftId(response.data.data._id);
        loadDraftData(response.data.data);
      }
    } catch (error) {
      console.error("Error checking for draft:", error);
    }
  };

  const loadDraftData = async (draftData) => {
    if (draftData.items && draftData.items.length > 0) {
      setLoading(true);
      // Convert draft items to the format expected by the component
      const convertedItems = await Promise.all(draftData.items.map(async (item) => {
        // Find the latest item details from allItems to get updated price and details
        const matchedItem = allItems.find(ai => ai.value === item.name?.value || ai.value === item.name);
        
        let expiry_date = '';
        const item_code = matchedItem?.item_code || item.name?.item_code;
        
        if (item_code) {
          try {
            const businessId = Fetched_Businesses[0]?._id ? [Fetched_Businesses[0]._id] : [];
            const response = await DashboardService.getBatchesForItem(item_code, businessId);
            if (response.data && Array.isArray(response.data.data) && response.data.data.length > 0) {
              const availableBatches = response.data.data.filter(b => b.quantity > 0);
              if (availableBatches.length > 0) {
                const nearestBatch = availableBatches.reduce((a, b) => new Date(a.expiry_date) < new Date(b.expiry_date) ? a : b);
                expiry_date = nearestBatch.expiry_date;
              }
            }
          } catch (error) {
            console.error("Error fetching batch for draft item:", error);
          }
        }

        return {
          name: matchedItem || item.name || { value: '', label: "Unknown Item" },
          quantity: item.quantity,
          price: matchedItem?.price || item.price || 0,
          expiry_date: expiry_date
        };
      }));
      setItems(convertedItems);
      // Update selectedItems with the loaded draft items
      const newSelectedItems = new Set(convertedItems.map(item => item.name?.value).filter(Boolean));
      setSelectedItems(newSelectedItems);
      setLoading(false);
    }

    // if (draftData.reason_of_use) {
    //   setReason_of_use(draftData.reason_of_use);
    // }
  };

  const saveDraft = async () => {
    if (items.length === 0) {
      return Alerts.swalErrorAlert(t("Please add at least one item before saving as draft"));
    }

    setLoading(true);
    try {
      const draftData = {
        items: items.map((item) => ({
          name: item.name,
          quantity: Number.parseInt(item.quantity),
          price: Number.parseFloat(item.name?.price || 0),
        })),
        user_id: userInfo._id,
        reason_of_use: reason_of_use,
        business_id: Fetched_Businesses[0]?._id,
      };

      const response = await DashboardService.SaveRFQDraft(draftData);
      if (response.data.message === "Unauthorized") {
        AuthService.logout();
        navigate("/login");
      } else {
        Alerts.swalSuccessAlert(t("Draft saved successfully"));
        setHasDraft(true);
        setDraftId(response.data.data._id);
        // Redirect to request_for_quotation page after saving draft
        setTimeout(() => {
          navigate('/request_for_quotation');
        }, 1500);
      }
    } catch (error) {
      Alerts.swalErrorAlert(t("Error saving draft"));
    } finally {
      setLoading(false);
    }
  };

  const deleteDraft = async () => {
    try {
      await DashboardService.DeleteRFQDraft(userInfo._id);
      setHasDraft(false);
      setDraftId(null);
      setItems([]);
      // setReason_of_use("");
      Alerts.swalSuccessAlert(t("Draft deleted successfully"));
      // Redirect to request_for_quotation page after deleting draft
      setTimeout(() => {
        navigate('/request_for_quotation');
      }, 1500);
    } catch (error) {
      Alerts.swalErrorAlert(t("Error deleting draft"));
    }
  };

  const fetchRfqDetails = async () => {
    setLoading(true);
    try {
      const payload = {
        functionName: "GetRFQDetails",
        postData: { _id: id }
      };
      const response = await DashboardService.GetRFQDetails(payload);
      if (response.data.status) {
        const rfqData = response.data.data[0];
        setOrderId(rfqData.rfq_id);

        const prefillItems = await Promise.all(rfqData.items.map(async (item) => {
          const matchedItem = allItems.find(ai => ai.value === item.item.toString());
          let expiry_date = '';

          if (matchedItem?.item_code) {
            try {
              const businessId = Fetched_Businesses[0]?._id ? [Fetched_Businesses[0]._id] : [];
              const batchResponse = await DashboardService.getBatchesForItem(matchedItem.item_code, businessId);
              if (batchResponse.data && Array.isArray(batchResponse.data.data) && batchResponse.data.data.length > 0) {
                const availableBatches = batchResponse.data.data.filter(b => b.quantity > 0);
                if (availableBatches.length > 0) {
                  const nearestBatch = availableBatches.reduce((a, b) => new Date(a.expiry_date) < new Date(b.expiry_date) ? a : b);
                  expiry_date = nearestBatch.expiry_date;
                }
              }
            } catch (error) {
              console.error("Error fetching batch for Order item:", error);
            }
          }

          return {
            name: matchedItem || { value: item.item.toString(), label: "Unknown Item" },
            quantity: item.quantity,
            ItemGroup: item.ItemGroup,
            status: item.status,
            price: item.price,
            expiry_date: expiry_date
          };
        }));

        setItems(prefillItems);
        const initialSelected = new Set(prefillItems.map(item => item.name.value));
        setSelectedItems(initialSelected);
      }
    } catch (error) {
      Alerts.swalErrorAlert(t("Error fetching Order details"));
    }
    setLoading(false);
  };

  const prefillOrderDetails = async () => {
    if (orderDetails?.items) {
      setLoading(true);
      const prefillItems = await Promise.all(orderDetails.items.map(async (item) => {
        const matchedItem = allItems.find(ai => ai.value === item.item.toString());
        let expiry_date = '';

        if (matchedItem?.item_code) {
          try {
            const businessId = Fetched_Businesses[0]?._id ? [Fetched_Businesses[0]._id] : [];
            const batchResponse = await DashboardService.getBatchesForItem(matchedItem.item_code, businessId);
            if (batchResponse.data && Array.isArray(batchResponse.data.data) && batchResponse.data.data.length > 0) {
              const availableBatches = batchResponse.data.data.filter(b => b.quantity > 0);
              if (availableBatches.length > 0) {
                const nearestBatch = availableBatches.reduce((a, b) => new Date(a.expiry_date) < new Date(b.expiry_date) ? a : b);
                expiry_date = nearestBatch.expiry_date;
              }
            }
          } catch (error) {
            console.error("Error fetching batch for order detail item:", error);
          }
        }

        return {
          name: matchedItem || { value: item.item.toString(), label: "Unknown Item" },
          quantity: item.quantity,
          ItemGroup: item.ItemGroup,
          status: item.status,
          price: item.price,
          expiry_date: expiry_date
        };
      }));

      setItems(prefillItems);
      const newSelectedItems = new Set(prefillItems.map(item => item.name.value));
      setSelectedItems(newSelectedItems);
      // setReason_of_use(orderDetails.reason_of_use);
      setLoading(false);
    }
  };

  const handleAddItem = () => {
    setItems([...items, {
      name: null, quantity: 1

    }]);
  };

  const handleItemChange = async (index, key, value) => {
    //iph todo: 47
    //console.log("index, key, value", index, key, value)
    const newItems = [...items];
    const oldItem = newItems[index];
    //iph todo: 47
    //console.log("value", value)
    if (key === 'name' && value?.item_code) {
      setLoading(true);
      try {
        const businessId = Fetched_Businesses[0]?._id ? [Fetched_Businesses[0]._id] : [];
        const response = await DashboardService.getBatchesForItem(value.item_code, businessId);
        if (response.data && Array.isArray(response.data.data) && response.data.data.length > 0) {
          const availableBatches = response.data.data.filter(b => b.quantity > 0);
          let nearestBatch = null;
          if (availableBatches.length > 0) {
            nearestBatch = availableBatches.reduce((a, b) => new Date(a.expiry_date) < new Date(b.expiry_date) ? a : b);
          }
          newItems[index] = {
            ...oldItem,
            [key]: value,
            expiry_date: nearestBatch ? nearestBatch.expiry_date : '',
          };
        } else {
          // No batches available - show blank expiry
          newItems[index] = {
            ...oldItem,
            [key]: value,
            expiry_date: '',
          };
        }
      } catch (error) {
        console.error("Error fetching batches:", error);
        // On error - show blank expiry
        newItems[index] = {
          ...oldItem,
          [key]: value,
          expiry_date: '',
        };
      }
      setLoading(false);
    } else {
      newItems[index] = { ...oldItem, [key]: value };
    }
    if (key === 'name') {
      setSelectedItems(prev => {
        const updated = new Set(prev);
        if (oldItem.name?.value) {
          updated.delete(oldItem.name.value);
        }
        if (value?.value) {
          updated.add(value.value);
        }
        return updated;
      });
    }
    setItems(newItems);
  };

  const handleRemoveItem = (index) => {
    const removedItem = items[index];
    const newItems = items.filter((_, i) => i !== index);
    setItems(newItems);

    if (removedItem.name?.value) {
      setSelectedItems(prev => {
        const updated = new Set(prev);
        updated.delete(removedItem.name.value);
        return updated;
      });
    }
  };

  const toggleTooltip = (index) => {
    setTooltipOpen(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (items.length === 0) {
        Alerts.swalErrorAlert(t("At least select 1 item."));
        return;
      }
      let price_check = 0;
      const validItems = items.map(item => {
        if (!item?.name?.value || !item?.quantity || item.quantity < 1) {
          return null;
        }
        price_check += parseFloat(item?.quantity) * parseFloat(item.name.price).toFixed(2)
        //iph todo: 47
        //console.log("item,item ", item);
        return {
          item: item.name.value,
          quantity: parseInt(item.quantity),
          category: item.name.category,
          ItemGroup: item.name.ItemGroup,
          status: item.name.status,
          price: item.name.price
        };
      }).filter(Boolean);

      if (validItems.length === 0) {
        Alerts.swalErrorAlert(t("Selected items are invalid. Please review and correct them."));
        return;
      }
      //iph todo: 47
      //console.log("price_check", price_check)
      let order_check = true;
      if (price_check < 300) {

        await Alerts.swalConfirmGeneralAlert(`Your current order does not meet the minimum free shipping requirement of RM300. 
          Current Order value is RM ${parseFloat(price_check).toFixed(2)}. Kindly review your cart before proceeding.`, 'Proceed').then(res => {
          // await Alerts.swalConfirmGeneralAlert(`Minimum order of RM300 required for free deliveries within Klang Valley, Current Order estimated Price ${price_check}. Do you want to proceed or order more?`).then(res => {
          //iph todo: 47
          //console.log("Resp:-", res)
          if (res === false) {
            order_check = false
          }
        })
      }

      let rfqData;
      let response;
      if (order_check) {
        if (isEditMode) {
          rfqData = {
            _id: id,
            items: validItems,
            reason_of_use: reason_of_use === "" ? 'Treatment of Patient' : reason_of_use,
            user_id: userInfo._id
          };
          response = await DashboardService.updateRFQ(rfqData);
        } else {
          const RFQ_ID = Math.floor(100000000 + Math.random() * 900000000);
          rfqData = {
            items: validItems,
            user_id: userInfo._id,
            rfq_id: RFQ_ID,
            reason_of_use: reason_of_use === "" ? 'Treatment of Patient' : reason_of_use,
            business_id: Fetched_Businesses[0]?._id,
            status: 'requested'
          };
          response = await DashboardService.saveRFQ(rfqData);
        }
        if (response.data.status) {
          // Delete draft after successful submission
          if (hasDraft) {
            try {
              await DashboardService.DeleteRFQDraft(userInfo._id);
              setHasDraft(false);
              setDraftId(null);
            } catch (error) {
              console.error("Error deleting draft after submission:", error);
            }
          }

          Alerts.swalSuccessAlert(t(isEditMode ? "Order updated successfully" : `Order #${rfqData.rfq_id} submitted successfully!`));
          navigate('/request_for_quotation');
        }
        //iph todo: 47
        //console.log("Order Successfully!")
      }

    } catch (error) {
      Alerts.swalErrorAlert(t(isEditMode ? "Error updating Order." : "Error submitting Order."));
    } finally {
      setLoading(false);
    }
  };

  const getPageTitle = () => {
    if (isEditMode) return t("Edit New Order");
    return orderDetails ? t("Re New Order") : t("New Order");
  };

  const getFormTitle = () => {
    if (isEditMode) return `${t("Edit Order")} #${orderId}`;
    return orderDetails ? t("Re New Order") : t("New Order");
  };

  return (
    <div className="app-main__outer">
      <div className="container mt-4">
        <div className="mb-4">
          <Button color="link" className="p-0 text-decoration-none" onClick={() => navigate(-1)}>
            <FaArrowLeft className="me-2" />
            <span className="text-dark fw-bold">{getPageTitle()}</span>
          </Button>
        </div>
        <Card className="rfq-card">
          <div className="p-4">
            <h4 className="mb-4 fw-bold">{getFormTitle()}</h4>
            <Form onSubmit={handleSubmit}>
              <LoadingOverlay active={loading} spinner={<DNALoader />}>
                {/* <Col md={12}>
                  <Row className="mb-3 align-items-center">
                    <Col md={2}>
                      <h5 className="fw-bold">{t("Reason of Use")}</h5>
                    </Col>
                    <Col md={4}>
                      <Input
                        type="text"
                        value={reason_of_use}
                        onChange={(e) => setReason_of_use(e.target.value)}
                      />
                    </Col>
                  </Row>
                </Col> */}
                <h5 className="mb-3 fw-bold">{isEditMode ? t("Edit Item(s)") : t("Add Item(s)")}</h5>
                <div className="item-list mb-3">
                  <Row className="mb-2 text-muted">
                    <Col md={3}>{t("Item(s)")}</Col>
                    <Col md={1}>{t("Quantity")}</Col>
                    <Col md={1}>{t("UOM")}</Col>
                    <Col md={2}>{t("Expiry Date")}</Col>
                    <Col md={2}>{t("Price")}</Col>
                    <Col md={2}>{t("Total Amount")}</Col>
                    <Col md={1} className="text-end">{t("Action")}</Col>
                  </Row>
                  {items.map((item, index) => {
                    const totalAmount = item.name?.price && item.quantity ?
                      (parseFloat(item.name.price) * parseInt(item.quantity)).toFixed(2) : '0.00';

                    return (
                      <Row key={index} className="mb-2 align-items-center">
                        <Col md={3}>
                          <div id={`tooltip-${index}`}>
                            <Select
                              value={item.name}
                              onChange={(selectedOption) => handleItemChange(index, 'name', selectedOption)}
                              options={allItems.filter(catItem => catItem.status === 'Active').map(catItem => ({
                                value: catItem.value,
                                label: catItem.label,
                                category: catItem.category,
                                ItemGroup: catItem.ItemGroup,
                                price: catItem.price,
                                item_code: catItem.item_code,
                                BaseUOM: catItem.BaseUOM
                              }))}
                              placeholder={t("Select Item")}
                              isSearchable
                            />
                          </div>
                          {item.name && (
                            <Tooltip
                              placement="top"
                              isOpen={tooltipOpen[index] || false}
                              target={`tooltip-${index}`}
                              toggle={() => toggleTooltip(index)}
                            >
                              {item.name.label}
                            </Tooltip>
                          )}
                        </Col>
                        <Col md={1}>
                          <Input
                            type="number"
                            min="1"
                            value={item.quantity}
                            onChange={(e) => handleItemChange(index, 'quantity', parseInt(e.target.value))}
                          />
                        </Col>
                        <Col md={1}>
                          <Input
                            type="text"
                            value={item.name?.BaseUOM || ''}
                            readOnly
                            className="form-control-plaintext"
                          />
                        </Col>
                        <Col md={2}>
                          <Input
                            type="text"
                            value={item.expiry_date ? moment(item.expiry_date).format('DD/MM/YYYY') : ''}
                            readOnly
                            className="form-control-plaintext"
                          />
                        </Col>
                        <Col md={2}>
                          <Input
                            type="text"
                            value={item.name?.price ? `RM ${parseFloat(item.name.price).toFixed(2)}` : ''}
                            readOnly
                            className="form-control-plaintext"
                          />
                        </Col>
                        <Col md={2}>
                          <Input
                            type="text"
                            value={`RM ${totalAmount}`}
                            readOnly
                            className="form-control-plaintext fw-bold"
                          />
                        </Col>
                        <Col md={1} className="text-end">
                          <Button color="link" className="p-0 text-danger" onClick={() => handleRemoveItem(index)}>
                            <FaTrash />
                          </Button>
                        </Col>
                      </Row>
                    );
                  })}
                </div>

                {/* Total Amount Section */}
                {(() => {
                  const totalAmount = items.reduce((total, item) => {
                    const itemTotal = item.name?.price && item.quantity ?
                      parseFloat(item.name.price) * parseInt(item.quantity) : 0;
                    return total + itemTotal;
                  }, 0);

                  return totalAmount > 0 ? (
                    <div className="mt-4 p-3 bg-light rounded">
                      <Row className="align-items-center">
                        <Col md={8} className="text-end">
                          <h5 className="mb-0 fw-bold">{t("Total Amount")}:</h5>
                        </Col>
                        <Col md={4}>
                          <div className="total-amount-display">
                            <h4 className="mb-0 fw-bold text-primary">
                              RM {totalAmount.toFixed(2)}
                            </h4>
                          </div>
                        </Col>
                      </Row>
                    </div>
                  ) : null;
                })()}

                <div className="d-flex mt-4">
                  <Button type="button" color="light" className="me-2 px-3" onClick={handleAddItem}>
                    <FaPlus className="me-2" /> {t("Add New")}
                  </Button>

                  {!isEditMode && (
                    <>
                      <Button
                        type="button"
                        color="secondary"
                        className="me-2 px-3"
                        onClick={saveDraft}
                        disabled={items.length === 0}
                      >
                        <FaEdit className="me-2" /> {t("Save as Draft")}
                      </Button>

                      {hasDraft && (
                        <Button
                          type="button"
                          color="danger"
                          className="me-2 px-3"
                          onClick={deleteDraft}
                        >
                          <FaTrash className="me-2" /> {t("Delete Draft")}
                        </Button>
                      )}
                    </>
                  )}

                  <Button type="submit" color="primary" className="px-3">
                    <FaCheck className="me-2" /> {isEditMode ? t("Update") : t("Submit")}
                  </Button>
                </div>
              </LoadingOverlay>
            </Form>
          </div>
        </Card>
        <OrderConfirmationModal
          isOpen={modalOpen}
          toggle={() => setModalOpen(!modalOpen)}
          orderId={orderId}
          message={isEditMode ? "Order Updated" : "Order Submitted"}
        />
      </div>
      <style>{`
        .app-main__outer {
          background-color: #f8f9fa;
          min-height: 100vh;
          padding-top: 20px;
        }
        .rfq-card {
          border-radius: 10px;
          box-shadow: 0 2px 10px rgba(0,0,0,0.05);
          border: none;
        }
        .form-select, .form-control {
          border-color: #e0e0e0;
          border-radius: 5px;
          padding: 10px;
        }
        .btn-light {
          background-color: #ffffff;
          border-color: #e0e0e0;
          color: #333;
        }
        .btn-primary {
          background-color: #3D607A;
          border-color: #3D607A;
        }
        .btn-secondary {
          background-color: #6c757d;
          border-color: #6c757d;
        }
        .btn-secondary:hover:not(:disabled) {
          background-color: #5a6268;
          border-color: #5a6268;
        }
        .btn-danger {
          background-color: #dc3545;
          border-color: #dc3545;
        }
        .btn-danger:hover:not(:disabled) {
          background-color: #c82333;
          border-color: #c82333;
        }
        .alert-info {
          background-color: #d1ecf1;
          border-color: #bee5eb;
          color: #0c5460;
        }
        .alert-info .fas {
          color: #0c5460;
        }
        .text-muted {
          color: #6c757d !important;
        }
        h4, h5 {
          color: #333;
        }
        .item-list {
          border-top: 1px solid #e0e0e0;
          border-bottom: 1px solid #e0e0e0;
          padding: 20px 0;
        }
        .order-confirmation-modal {
          display: flex !important;
          align-items: center;
          justify-content: center;
        }
        .order-confirmation-modal .modal-content {
          border-radius: 20px;
          border: none;
          box-shadow: 0 5px 15px rgba(0,0,0,0.1);
        }
        .confirmation-icon {
          width: 60px;
          height: 60px;
          background-color: #4CAF50;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto 1.5rem;
        }
        .order-id {
          font-size: 2rem;
          font-weight: bold;
        }
        .tooltip {
          font-size: 12px;
        }
        .tooltip-inner {
          max-width: 300px;
          text-align: left;
          background-color: #333;
          color: #fff;
          border-radius: 4px;
          padding: 8px 12px;
        }
      `}</style>
    </div>
  );
};

export default RFQForm;